import { useMutation, useQueryClient } from '@tanstack/react-query';
import { askForPush } from '../native/push';
import {
  tasksApi,
  projectsApi,
  habitsApi,
  labelsApi,
  documentsApi,
  webhooksApi,
  focusTimeApi,
  settingsApi,
  trashApi,
  notificationsApi,
  taskStatusesApi,
  taskTypesApi,
  teamsApi,
  profileApi,
  billingApi,
  gmailApi,
  googleCalendarApi,
  adminApi,
  customFieldsApi,
  unreadsApi,
  botsApi,
} from '../services/api';
import {
  taskKeys,
  botKeys,
  projectKeys,
  habitKeys,
  labelKeys,
  documentKeys,
  webhookKeys,
  focusTimeKeys,
  settingsKeys,
  trashKeys,
  notificationKeys,
  customStatusKeys,
  customFieldKeys,
  taskTypeKeys,
  teamKeys,
  billingKeys,
  gmailKeys,
  googleCalendarKeys,
  adminKeys,
  unreadKeys,
} from '../lib/queryKeys';

// Mutations rely on the global MutationCache.onSuccess in queryClient.js to
// invalidate every cached query. Hooks below add optimistic updates where
// they meaningfully reduce perceived latency (single-item edits and deletes
// against the active list).

const unwrap = (p) => p.then((r) => r.data);

// Generic optimistic-list helpers. Take a queryKey root and return mutate
// callbacks that snapshot every cached list under that key, mutate the
// cache, and offer a rollback. Use them to keep mutation hooks terse.
const makeOptimisticUpdate = (qc, listsKey, mergeFn) => async (vars) => {
  await qc.cancelQueries({ queryKey: listsKey });
  const snapshots = qc.getQueriesData({ queryKey: listsKey });
  snapshots.forEach(([key, list]) => {
    if (!Array.isArray(list)) return;
    qc.setQueryData(key, mergeFn(list, vars));
  });
  return { snapshots };
};

const rollback = (qc) => (_err, _vars, ctx) => {
  ctx?.snapshots?.forEach(([key, list]) => qc.setQueryData(key, list));
};

// ── Tasks ─────────────────────────────────────────────────────────────────

export const useCreateTask = () =>
  useMutation({ mutationFn: (data) => unwrap(tasksApi.create(data)) });

export const useUpdateTask = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => unwrap(tasksApi.update(id, data)),
    onMutate: makeOptimisticUpdate(qc, taskKeys.lists(), (list, { id, data }) =>
      list.map((t) => (t.id === id ? { ...t, ...data } : t))
    ),
    onError: rollback(qc),
  });
};

export const useDeleteTask = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => unwrap(tasksApi.delete(id)),
    onMutate: makeOptimisticUpdate(qc, taskKeys.lists(), (list, id) =>
      list.filter((t) => t.id !== id)
    ),
    onError: rollback(qc),
  });
};

export const useDuplicateTask = () =>
  useMutation({ mutationFn: (id) => unwrap(tasksApi.duplicate(id)) });

export const useBulkUpdateTasks = () =>
  useMutation({ mutationFn: (data) => unwrap(tasksApi.bulkUpdate(data)) });

export const useBulkDeleteTasks = () =>
  useMutation({ mutationFn: (data) => unwrap(tasksApi.bulkDelete(data)) });

export const useAddComment = () =>
  useMutation({
    mutationFn: ({ taskId, content }) =>
      unwrap(tasksApi.addComment(taskId, content)),
  });

export const useDeleteComment = () =>
  useMutation({ mutationFn: (commentId) => unwrap(tasksApi.deleteComment(commentId)) });

// ── Projects ──────────────────────────────────────────────────────────────

export const useCreateProject = () =>
  useMutation({ mutationFn: (data) => unwrap(projectsApi.create(data)) });

export const useUpdateProject = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => unwrap(projectsApi.update(id, data)),
    onMutate: makeOptimisticUpdate(qc, projectKeys.lists(), (list, { id, data }) =>
      list.map((p) => (p.id === id ? { ...p, ...data } : p))
    ),
    onError: rollback(qc),
  });
};

export const useDeleteProject = () =>
  useMutation({ mutationFn: (id) => unwrap(projectsApi.delete(id)) });

export const useReorderProjects = () =>
  useMutation({ mutationFn: (items) => unwrap(projectsApi.reorder(items)) });

// ── Project sections ──────────────────────────────────────────────────────

export const useCreateSection = () =>
  useMutation({
    mutationFn: ({ projectId, data }) =>
      unwrap(projectsApi.createSection(projectId, data)),
  });

export const useUpdateSection = () =>
  useMutation({
    mutationFn: ({ id, data }) => unwrap(projectsApi.updateSection(id, data)),
  });

export const useDeleteSection = () =>
  useMutation({ mutationFn: (id) => unwrap(projectsApi.deleteSection(id)) });

// ── Habits ────────────────────────────────────────────────────────────────

export const useCreateHabit = () =>
  useMutation({ mutationFn: (data) => unwrap(habitsApi.create(data)) });

export const useUpdateHabit = () =>
  useMutation({
    mutationFn: ({ id, data }) => unwrap(habitsApi.update(id, data)),
  });

export const useDeleteHabit = () =>
  useMutation({ mutationFn: (id) => unwrap(habitsApi.delete(id)) });

export const useCompleteHabit = () =>
  useMutation({ mutationFn: (id) => unwrap(habitsApi.complete(id)) });

// ── Labels ────────────────────────────────────────────────────────────────

export const useCreateLabel = () =>
  useMutation({ mutationFn: (data) => unwrap(labelsApi.create(data)) });

export const useUpdateLabel = () =>
  useMutation({
    mutationFn: ({ id, data }) => unwrap(labelsApi.update(id, data)),
  });

export const useDeleteLabel = () =>
  useMutation({ mutationFn: (id) => unwrap(labelsApi.delete(id)) });

// ── Documents ─────────────────────────────────────────────────────────────

export const useCreateDocument = () =>
  useMutation({ mutationFn: (data) => unwrap(documentsApi.create(data)) });

export const useUpdateDocument = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => unwrap(documentsApi.update(id, data)),
    onMutate: async ({ id, data }) => {
      await qc.cancelQueries({ queryKey: documentKeys.detail(id) });
      const prev = qc.getQueryData(documentKeys.detail(id));
      if (prev) qc.setQueryData(documentKeys.detail(id), { ...prev, ...data });
      const listSnaps = qc.getQueriesData({ queryKey: documentKeys.lists() });
      listSnaps.forEach(([key, list]) => {
        if (!Array.isArray(list)) return;
        qc.setQueryData(
          key,
          list.map((d) => (d.id === id ? { ...d, ...data } : d))
        );
      });
      return { prev, listSnaps };
    },
    onError: (_err, { id }, ctx) => {
      if (ctx?.prev) qc.setQueryData(documentKeys.detail(id), ctx.prev);
      ctx?.listSnaps?.forEach(([key, list]) => qc.setQueryData(key, list));
    },
  });
};

export const useDeleteDocument = () =>
  useMutation({ mutationFn: (id) => unwrap(documentsApi.delete(id)) });

// ── Webhooks ──────────────────────────────────────────────────────────────

export const useCreateWebhook = () =>
  useMutation({ mutationFn: (data) => unwrap(webhooksApi.create(data)) });

export const useUpdateWebhook = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => unwrap(webhooksApi.update(id, data)),
    onMutate: makeOptimisticUpdate(qc, webhookKeys.lists(), (list, { id, data }) =>
      list.map((w) => (w.id === id ? { ...w, ...data } : w))
    ),
    onError: rollback(qc),
  });
};

export const useDeleteWebhook = () =>
  useMutation({ mutationFn: (id) => unwrap(webhooksApi.delete(id)) });

export const useTestWebhook = () =>
  useMutation({
    mutationFn: (id) => unwrap(webhooksApi.test(id)),
    meta: { invalidates: false },
  });

// ── Focus time ────────────────────────────────────────────────────────────

export const useUpdateFocusTime = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => unwrap(focusTimeApi.update(data)),
    onMutate: async (data) => {
      await qc.cancelQueries({ queryKey: focusTimeKeys.current() });
      const prev = qc.getQueryData(focusTimeKeys.current());
      if (prev) qc.setQueryData(focusTimeKeys.current(), { ...prev, ...data });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(focusTimeKeys.current(), ctx.prev);
    },
  });
};

// ── Settings ──────────────────────────────────────────────────────────────

export const useUpdateSettings = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => unwrap(settingsApi.update(data)),
    onMutate: async (data) => {
      await qc.cancelQueries({ queryKey: settingsKeys.current() });
      const prev = qc.getQueryData(settingsKeys.current());
      if (prev) {
        // Shallow-merge top-level keys, plus dotted-key paths used by the
        // call sites (e.g. 'ui_preferences.task_detail_view_mode').
        const next = { ...prev };
        Object.entries(data).forEach(([k, v]) => {
          if (k.includes('.')) {
            const segments = k.split('.');
            let cur = next;
            for (let i = 0; i < segments.length - 1; i++) {
              const seg = segments[i];
              cur[seg] = { ...(cur[seg] || {}) };
              cur = cur[seg];
            }
            cur[segments[segments.length - 1]] = v;
          } else {
            next[k] = v;
          }
        });
        qc.setQueryData(settingsKeys.current(), next);
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(settingsKeys.current(), ctx.prev);
    },
  });
};

export const useClearPlanner = () =>
  useMutation({ mutationFn: () => unwrap(settingsApi.clearPlanner()) });

// ── Trash ─────────────────────────────────────────────────────────────────

export const useRestoreTask = () =>
  useMutation({ mutationFn: (id) => unwrap(trashApi.restoreTask(id)) });

// Permanent deletes free R2 storage + decrement storage_used_bytes server-side,
// so the Subscription page's storage indicator must be invalidated to refetch.
export const useDeleteTaskPermanently = () =>
  useMutation({
    mutationFn: (id) => unwrap(trashApi.deleteTaskPermanently(id)),
    meta: { invalidates: [billingKeys.all] },
  });

export const useRestoreProject = () =>
  useMutation({ mutationFn: (id) => unwrap(trashApi.restoreProject(id)) });

export const useDeleteProjectPermanently = () =>
  useMutation({
    mutationFn: (id) => unwrap(trashApi.deleteProjectPermanently(id)),
    meta: { invalidates: [billingKeys.all] },
  });

export const useRestoreDocument = () =>
  useMutation({ mutationFn: (id) => unwrap(trashApi.restoreDocument(id)) });

export const useDeleteDocumentPermanently = () =>
  useMutation({
    mutationFn: (id) => unwrap(trashApi.deleteDocumentPermanently(id)),
    meta: { invalidates: [billingKeys.all] },
  });

export const useRestoreSection = () =>
  useMutation({ mutationFn: (id) => unwrap(trashApi.restoreSection(id)) });

export const useDeleteSectionPermanently = () =>
  useMutation({
    mutationFn: (id) => unwrap(trashApi.deleteSectionPermanently(id)),
    meta: { invalidates: [billingKeys.all] },
  });

export const useEmptyTrash = () =>
  useMutation({
    mutationFn: () => unwrap(trashApi.emptyTrash()),
    meta: { invalidates: [billingKeys.all] },
  });

// ── Notifications ─────────────────────────────────────────────────────────

export const useMarkNotificationRead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => unwrap(notificationsApi.markRead(id)),
    onMutate: makeOptimisticUpdate(qc, notificationKeys.lists(), (list, id) =>
      list.map((n) => (n.id === id ? { ...n, is_read: true } : n))
    ),
    onError: rollback(qc),
  });
};

export const useMarkAllNotificationsRead = () =>
  useMutation({ mutationFn: () => unwrap(notificationsApi.markAllRead()) });

export const useDeleteNotification = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => unwrap(notificationsApi.delete(id)),
    onMutate: makeOptimisticUpdate(qc, notificationKeys.lists(), (list, id) =>
      list.filter((n) => n.id !== id)
    ),
    onError: rollback(qc),
  });
};

export const useUpdateNotificationSettings = () =>
  useMutation({ mutationFn: (data) => unwrap(notificationsApi.updateSettings(data)) });

// ── Custom statuses ───────────────────────────────────────────────────────

export const useCreateCustomStatus = () =>
  useMutation({
    mutationFn: ({ data, teamId }) => taskStatusesApi.create(data, teamId),
  });

export const useUpdateCustomStatus = () =>
  useMutation({
    mutationFn: ({ id, data }) => taskStatusesApi.update(id, data),
  });

export const useDeleteCustomStatus = () =>
  useMutation({ mutationFn: (id) => taskStatusesApi.delete(id) });

export const useReorderCustomStatuses = () =>
  useMutation({
    mutationFn: ({ order, teamId }) => taskStatusesApi.reorder(order, teamId),
  });

// ── Custom fields ─────────────────────────────────────────────────────────

export const useCreateCustomField = () =>
  useMutation({ mutationFn: (data) => unwrap(customFieldsApi.create(data)) });

export const useUpdateCustomField = () =>
  useMutation({
    mutationFn: ({ id, data }) => unwrap(customFieldsApi.update(id, data)),
  });

export const useDeleteCustomField = () =>
  useMutation({ mutationFn: (id) => unwrap(customFieldsApi.delete(id)) });

// ── Task types ────────────────────────────────────────────────────────────

export const useCreateTaskType = () =>
  useMutation({ mutationFn: (data) => unwrap(taskTypesApi.create(data)) });

export const useUpdateTaskType = () =>
  useMutation({
    mutationFn: ({ id, data }) => unwrap(taskTypesApi.update(id, data)),
  });

export const useDeleteTaskType = () =>
  useMutation({ mutationFn: (id) => unwrap(taskTypesApi.delete(id)) });

export const useReorderTaskTypes = () =>
  useMutation({ mutationFn: (order) => unwrap(taskTypesApi.reorder(order)) });

// ── Teams ─────────────────────────────────────────────────────────────────

export const useCreateTeam = () =>
  useMutation({ mutationFn: (data) => unwrap(teamsApi.create(data)) });

export const useUpdateTeam = () =>
  useMutation({
    mutationFn: ({ id, data }) => unwrap(teamsApi.update(id, data)),
  });

export const useDeleteTeam = () =>
  useMutation({ mutationFn: (id) => unwrap(teamsApi.delete(id)) });

export const useInviteToTeam = () =>
  useMutation({
    mutationFn: ({ teamId, data }) => unwrap(teamsApi.invite(teamId, data)),
  });

export const useCancelTeamInvite = () =>
  useMutation({
    mutationFn: ({ teamId, inviteId }) =>
      unwrap(teamsApi.cancelInvite(teamId, inviteId)),
  });

export const useRemoveTeamMember = () =>
  useMutation({
    mutationFn: ({ teamId, userId }) =>
      unwrap(teamsApi.removeMember(teamId, userId)),
  });

export const useUpdateTeamMemberRole = () =>
  useMutation({
    mutationFn: ({ teamId, userId, role }) =>
      unwrap(teamsApi.updateMemberRole(teamId, userId, role)),
  });

export const useShareTeam = () =>
  useMutation({ mutationFn: (teamId) => unwrap(teamsApi.share(teamId)) });

export const useRevokeTeamShare = () =>
  useMutation({ mutationFn: (teamId) => unwrap(teamsApi.revokeShare(teamId)) });

export const useUpdateTeamPublicSettings = () =>
  useMutation({
    mutationFn: ({ teamId, data }) =>
      unwrap(teamsApi.updatePublicSettings(teamId, data)),
  });

// ── Profile ───────────────────────────────────────────────────────────────

export const useUploadAvatar = () =>
  useMutation({ mutationFn: (file) => unwrap(profileApi.uploadAvatar(file)) });

export const useDeleteAvatar = () =>
  useMutation({ mutationFn: () => unwrap(profileApi.deleteAvatar()) });

export const useUpdateProfile = () =>
  useMutation({ mutationFn: (data) => unwrap(profileApi.updateProfile(data)) });

export const useChangePassword = () =>
  useMutation({
    mutationFn: (data) => unwrap(profileApi.changePassword(data)),
    meta: { invalidates: false },
  });

// ── Billing ───────────────────────────────────────────────────────────────

export const useCreateCheckoutSession = () =>
  useMutation({
    mutationFn: (data) => unwrap(billingApi.createCheckoutSession(data)),
    meta: { invalidates: false },
  });

export const useCreatePortalSession = () =>
  useMutation({
    mutationFn: (data = {}) => unwrap(billingApi.createPortalSession(data)),
    meta: { invalidates: false },
  });

export const useSyncBilling = () =>
  useMutation({
    mutationFn: () => unwrap(billingApi.syncFromStripe()),
    meta: { invalidates: [billingKeys.all] },
  });

// ── Gmail ─────────────────────────────────────────────────────────────────

export const useGmailExchangeCode = () =>
  useMutation({
    mutationFn: (code) => unwrap(gmailApi.exchangeCode(code)),
    meta: { invalidates: [gmailKeys.all] },
  });

export const useGmailDisconnect = () =>
  useMutation({
    mutationFn: () => unwrap(gmailApi.disconnect()),
    meta: { invalidates: [gmailKeys.all] },
  });

export const useGmailSync = () =>
  useMutation({
    mutationFn: () => unwrap(gmailApi.sync()),
    meta: { invalidates: [gmailKeys.all] },
  });

export const useCreateTaskFromEmail = () =>
  useMutation({
    mutationFn: ({ messageId, data }) =>
      unwrap(gmailApi.createTaskFromEmail(messageId, data)),
  });

export const useCreateGmailRule = () =>
  useMutation({ mutationFn: (data) => unwrap(gmailApi.createRule(data)) });

export const useUpdateGmailRule = () =>
  useMutation({
    mutationFn: ({ id, data }) => unwrap(gmailApi.updateRule(id, data)),
  });

export const useDeleteGmailRule = () =>
  useMutation({ mutationFn: (id) => unwrap(gmailApi.deleteRule(id)) });

export const useLinkEmailToTask = () =>
  useMutation({
    mutationFn: ({ taskId, threadId }) =>
      unwrap(gmailApi.linkEmailToTask(taskId, threadId)),
  });

export const useUnlinkEmailFromTask = () =>
  useMutation({ mutationFn: (taskId) => unwrap(gmailApi.unlinkEmailFromTask(taskId)) });

// ── Google Calendar ───────────────────────────────────────────────────────

export const useGoogleCalendarExchange = () =>
  useMutation({
    mutationFn: (code) => unwrap(googleCalendarApi.exchangeCode(code)),
    meta: { invalidates: [googleCalendarKeys.all] },
  });

export const useGoogleCalendarDisconnect = () =>
  useMutation({
    mutationFn: () => unwrap(googleCalendarApi.disconnect()),
    meta: { invalidates: [googleCalendarKeys.all] },
  });

export const useGoogleCalendarSync = () =>
  useMutation({
    mutationFn: () => unwrap(googleCalendarApi.sync()),
    meta: { invalidates: [googleCalendarKeys.all, taskKeys.all] },
  });

export const useGoogleCalendarUpdateSettings = () =>
  useMutation({
    mutationFn: (settings) => unwrap(googleCalendarApi.updateSettings(settings)),
  });

// ── Admin ─────────────────────────────────────────────────────────────────

// Admin changes refresh the whole console (lists, the person's detail, the audit log).
export const useSetUserRole = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }) => unwrap(adminApi.setRole(userId, role)),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.all }),
  });
};

export const useSetUserPlan = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, data }) => unwrap(adminApi.setPlan(userId, data)),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.all }),
  });
};

// ---------------------------------------------------------------------------
// Unread comment indicators
// ---------------------------------------------------------------------------
// markRead fires while the user scrolls a comment thread, so it snapshots the
// cache for rollback and lets the settled refetch supply exact counts. A failed
// request leaves the server rows in place, so the next refetch puts the
// indicator back: the failure mode is "you see it again", never silent loss.

export const useMarkCommentsRead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentIds) => unwrap(unreadsApi.markRead(commentIds)),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: unreadKeys.summary() });
      return { previous: qc.getQueryData(unreadKeys.summary()) };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(unreadKeys.summary(), ctx.previous);
    },
    onSettled: () =>
      qc.invalidateQueries({ queryKey: unreadKeys.all, cancelRefetch: false }),
  });
};

export const useMarkCommentsUnread = () =>
  useMutation({
    mutationFn: (commentIds) => unwrap(unreadsApi.markUnread(commentIds)),
    meta: { invalidates: [unreadKeys.all] },
  });

// Clearing a whole task is the one case where the client knows enough to
// recompute the rollup itself, so this one patches optimistically: the badge
// and the project dot vanish on click rather than a round trip later.
export const useMarkTaskCommentsRead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId) => unwrap(unreadsApi.markTaskRead(taskId)),
    onMutate: async (taskId) => {
      await qc.cancelQueries({ queryKey: unreadKeys.summary() });
      const previous = qc.getQueryData(unreadKeys.summary());
      qc.setQueryData(unreadKeys.summary(), (old) => {
        if (!old?.tasks?.[taskId]) return old;
        const { [taskId]: removed, ...tasks } = old.tasks;
        const projects = { ...old.projects };
        const pid = removed.project_id;
        if (pid && projects[pid]) {
          const tasksLeft = projects[pid].tasks - 1;
          if (tasksLeft <= 0) {
            delete projects[pid];
          } else {
            projects[pid] = {
              tasks: tasksLeft,
              mentions: Math.max(0, projects[pid].mentions - removed.mentions),
            };
          }
        }
        return { ...old, tasks, projects };
      });
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(unreadKeys.summary(), ctx.previous);
    },
    onSettled: () =>
      qc.invalidateQueries({ queryKey: unreadKeys.all, cancelRefetch: false }),
  });
};

// Re-export key factories so callers can do targeted invalidation if needed.
export {
  taskKeys,
  projectKeys,
  habitKeys,
  labelKeys,
  documentKeys,
  webhookKeys,
  focusTimeKeys,
  settingsKeys,
  trashKeys,
  notificationKeys,
  customStatusKeys,
  customFieldKeys,
  taskTypeKeys,
  teamKeys,
  billingKeys,
  gmailKeys,
  googleCalendarKeys,
  adminKeys,
};

// ── Bots ──────────────────────────────────────────────────────────────────
// The global MutationCache invalidates cached queries on success, so model
// pickers and project bot lists refresh without extra wiring.

export const useCreateBot = () =>
  useMutation({ mutationFn: (data) => unwrap(botsApi.create(data)) });

export const useUpdateBot = () =>
  useMutation({ mutationFn: ({ id, data }) => unwrap(botsApi.update(id, data)) });

export const useMoveBot = () =>
  useMutation({ mutationFn: ({ id, teamId }) => unwrap(botsApi.move(id, teamId)) });

export const useDeleteBot = () =>
  useMutation({ mutationFn: (id) => unwrap(botsApi.delete(id)) });

export const useSaveAISettings = () =>
  useMutation({ mutationFn: ({ teamId, data }) => unwrap(botsApi.putAISettings(teamId, data)) });

export const useAddProjectBot = () =>
  useMutation({ mutationFn: ({ projectId, data }) => unwrap(botsApi.addToProject(projectId, data)) });

export const useRemoveProjectBot = () =>
  useMutation({ mutationFn: ({ projectId, botId }) => unwrap(botsApi.removeFromProject(projectId, botId)) });

// Handing work to a bot is the moment the app asks for notifications (once, src/native/push.js).
export const useAssignTaskBot = () =>
  useMutation({
    mutationFn: ({ taskId, botId }) => unwrap(botsApi.assignTask(taskId, botId)),
    onSuccess: (_data, { botId }) => { if (botId) askForPush(); },
  });

export const useCreateBotChat = () =>
  useMutation({ mutationFn: ({ botId, projectId }) => unwrap(botsApi.createChat(botId, projectId)) });

// BusyBee itself, the person's main chat: made the first time they open it (backend agents/queen.py).
export const useOpenQueen = () =>
  useMutation({ mutationFn: () => unwrap(botsApi.openQueen()), meta: { invalidates: [botKeys.mine()] } });

export const useMoveBotChat = () =>
  useMutation({ mutationFn: ({ chatId, projectId }) => unwrap(botsApi.moveChat(chatId, projectId)) });

export const useAddMemory = () =>
  useMutation({ mutationFn: ({ projectId, content, target = 'project' }) =>
    unwrap(botsApi.addMemory(projectId, { content, target })) });

export const useForgetMemory = () =>
  useMutation({ mutationFn: (entryId) => unwrap(botsApi.forgetMemory(entryId)) });

export const useUndoMemoryEvent = () =>
  useMutation({ mutationFn: (eventId) => unwrap(botsApi.undoMemoryEvent(eventId)) });

export const useWorkspaceAction = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, action, note }) => unwrap({
      start: () => botsApi.startWorkspace(projectId),
      pause: () => botsApi.pauseWorkspace(projectId),
      takeOver: () => botsApi.takeOverWorkspace(projectId),
      giveBack: () => botsApi.giveBackWorkspace(projectId, note),
      remove: () => botsApi.removeWorkspace(projectId),
    }[action]()),
    // The answer is the computer as it is now: show it at once (who is in control), so
    // the screen takes the keyboard without waiting for the refetch (none after a removal).
    onSuccess: (data, { projectId }) => {
      if (data && 'workspace' in data) {
        qc.setQueryData(botKeys.workspace(projectId), (prev) => (prev ? { ...prev, workspace: data.workspace } : prev));
      }
    },
  });
};

export const useConnectService = () =>
  useMutation({ mutationFn: ({ botId, ...data }) => unwrap(botsApi.connect(botId, data)) });

export const useImportConnections = () =>
  useMutation({ mutationFn: ({ botId, ...data }) => unwrap(botsApi.importConnections(botId, data)) });

export const useReconnectService = () =>
  useMutation({ mutationFn: (id) => unwrap(botsApi.reconnect(id)) });

export const useRefreshConnection = () =>
  useMutation({ mutationFn: (id) => unwrap(botsApi.refreshConnection(id)) });

export const useUpdateConnection = () =>
  useMutation({ mutationFn: ({ id, ...data }) => unwrap(botsApi.updateConnection(id, data)) });

export const useDisconnectService = () =>
  useMutation({ mutationFn: (id) => unwrap(botsApi.disconnect(id)) });

export const useScheduleAction = () =>
  useMutation({
    mutationFn: ({ action, id, botId, data }) => unwrap({
      create: () => botsApi.createSchedule(botId, data),
      update: () => botsApi.updateSchedule(id, data),
      remove: () => botsApi.deleteSchedule(id),
      runNow: () => botsApi.runSchedule(id),
    }[action]()),
  });

export const useLearningAction = () =>
  useMutation({
    mutationFn: ({ action, id, projectId, text, data }) => unwrap({
      accept: () => botsApi.acceptProposal(id),
      reject: () => botsApi.rejectProposal(id),
      addRule: () => botsApi.addRule(projectId, text),
      autoLearn: () => botsApi.setAutoLearn(projectId, data),
      deleteRule: () => botsApi.deleteRule(id),
      updateSkill: () => botsApi.updateSkill(id, data),
      shareSkill: () => botsApi.shareSkill(id),
      removeLibrarySkill: () => botsApi.removeLibrarySkill(id),
      createSkill: () => botsApi.createSkill(projectId, data),
      restoreVersion: () => botsApi.restoreSkill(id, data),
      undo: () => botsApi.undoProposal(id),
      tidy: () => botsApi.tidySkills(projectId),
    }[action]()),
  });

export const useAddProfileFact = () =>
  useMutation({ mutationFn: (content) => unwrap(botsApi.addProfileFact(content)) });

export const useCancelRun = () =>
  useMutation({ mutationFn: (runId) => unwrap(botsApi.cancelRun(runId)) });

export const useShareSecret = () =>
  useMutation({ mutationFn: ({ approvalId, value, remember }) => unwrap(botsApi.shareSecret(approvalId, value, remember)) });

export const useDeleteSecret = () =>
  useMutation({ mutationFn: (id) => unwrap(botsApi.deleteSecret(id)) });

export const useDecideApproval = () =>
  useMutation({
    mutationFn: ({ approvalId, approve, note, answer, edits }) =>
      unwrap(botsApi.decideApproval(approvalId, approve, note, { answer, edits })),
  });

export const useReviewTaskWork = () =>
  useMutation({ mutationFn: ({ taskId, decision, feedback }) => unwrap(botsApi.reviewTask(taskId, decision, feedback)) });

export const useDeleteBotChat = () =>
  useMutation({ mutationFn: (chatId) => unwrap(botsApi.deleteChat(chatId)) });

export const useUpdateBotChat = () =>
  useMutation({ mutationFn: ({ chatId, ...data }) => unwrap(botsApi.updateChat(chatId, data)) });

export const useSaveAnswerAsDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageId) => unwrap(botsApi.saveAsDocument(messageId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: botKeys.all });
      queryClient.invalidateQueries({ queryKey: documentKeys.all });
    },
  });
};

export const useSetProjectBotsPaused = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, paused }) => unwrap(botsApi.setProjectPaused(projectId, paused)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: botKeys.all }),
  });
};

export const useSetProjectBotBudget = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, credits }) => unwrap(botsApi.setProjectBudget(projectId, credits)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: botKeys.all }),
  });
};

export const useSetServiceBotsPaused = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (paused) => unwrap(adminApi.setBotsPause(paused)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: botKeys.all }),
  });
};

// Thumbs up/down shows at once; the server keeps it.
/** Take back a message the bot has not read yet (backend agents/steering.py). */
export const useCancelWaitingMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId }) => unwrap(botsApi.cancelWaiting(messageId)),
    onSettled: (_data, _err, { chatId }) => queryClient.invalidateQueries({ queryKey: botKeys.chat(chatId) }),
  });
};

export const useRateAnswer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, value, note }) => unwrap(botsApi.rateAnswer(messageId, value, note)),
    onMutate: ({ chatId, messageId, value }) => {
      queryClient.setQueryData(botKeys.chat(chatId), (old) => old && {
        ...old, messages: old.messages.map((m) => (m.id === messageId ? { ...m, feedback: value } : m)),
      });
    },
    onError: (_err, { chatId }) => queryClient.invalidateQueries({ queryKey: botKeys.chat(chatId) }),
  });
};

// Reporting an answer as offensive or harmful (Google Play's AI-generated content policy).
export const useReportAnswer = () =>
  useMutation({ mutationFn: ({ messageId, note }) => unwrap(botsApi.reportAnswer(messageId, note)) });

export const useSaveMyInstructions = () =>
  useMutation({ mutationFn: (text) => unwrap(botsApi.saveMyInstructions(text)) });

export const useSaveMyDefaultModel = () =>
  useMutation({ mutationFn: (modelId) => unwrap(botsApi.saveMyDefaultModel(modelId)) });

export const useSaveByok = () =>
  useMutation({ mutationFn: ({ provider, key }) => unwrap(botsApi.saveByok(provider, key)) });

export const useDeleteByok = () =>
  useMutation({ mutationFn: ({ provider }) => unwrap(botsApi.deleteByok(provider)) });

export const useRetryBotChat = () =>
  useMutation({ mutationFn: (chatId) => unwrap(botsApi.retryChat(chatId)) });

// The server's reply (message + queued run) is added to the chat right away so
// the bot's live answer can start streaming without a refetch.
export const useSendBotMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ chatId, content, fileIds, modelId }) => unwrap(botsApi.sendMessage(chatId, content, fileIds, modelId)),
    onSuccess: async ({ message, reply, run, chat }) => {
      // A refetch started before this message was sent would bring the chat back without
      // it: stop it, add the message and the bot's reply, then read the chat again.
      const key = botKeys.chat(chat.id);
      await queryClient.cancelQueries({ queryKey: key });
      queryClient.setQueryData(key, (old) => {
        const messages = (old?.messages || []).filter((m) => m.id !== message.id && m.id !== reply?.id);
        return {
          ...old,
          chat: { ...(old?.chat || {}), ...chat },
          messages: [...messages, message, ...(reply ? [reply] : [])],
          runs: [...(old?.runs || []).filter((r) => r.id !== run.id), { ...run, actions: [] }],
        };
      });
      queryClient.invalidateQueries({ queryKey: [...botKeys.all, 'chats', chat.bot_id] });
      queryClient.invalidateQueries({ queryKey: botKeys.allChats().slice(0, 3) });  // the one history (Chats)
      askForPush();
    },
  });
};
